import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { Alert, AlertTitle, CssBaseline, Grid } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import "./App.scss";
import { Disclaimer, Header } from "./components";
import { IDDLE_TIME } from "./config";
import { ERoute } from "./constants";
import { SUPPORTED_VIDEO_EXTENSION } from "./constants/configs";
import { useLocalStorage } from "./hooks";
import { ScreenSaverPage } from "./pages";
import { useVideoController } from "./stores";
import useUiStore from "./stores/useUiStore";
import theme from "./theme";

const App = () => {
  const { t } = useTranslation("component");
  const queryClient = new QueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  const timeOutRef = useRef<NodeJS.Timeout>();

  const { isMobile } = useUiStore();
  const { videoRef } = useVideoController();

  const [isOnline, toggleNetwork] = useState<boolean>(navigator.onLine);
  const [isShowScreenSaver, toggleScreeSaver] = useState<boolean>(false);
  const { screenSaverTimeout } = useLocalStorage();

  const handleToggle = () => {
    toggleNetwork(navigator.onLine);
  };

  const renderOfflineAlert = () => {
    return (
      !isOnline &&
      isMobile && (
        <Alert severity="warning">
          <AlertTitle>{t("app.warning")}</AlertTitle>
          {t("app.connectToWifiHotspotOrMobile")}
        </Alert>
      )
    );
  };

  useEffect(() => {
    if (location.pathname === "/") {
      navigate("/home");
    }
    window.addEventListener("online", handleToggle);
    window.addEventListener("offline", handleToggle);
    return () => {
      window.removeEventListener("online", handleToggle);
      window.removeEventListener("offline", handleToggle);
    };
  }, []);

  const startTimer = () => {
    if (timeOutRef) {
      clearTimeout(timeOutRef.current);
      timeOutRef.current = setTimeout(() => {
        toggleScreeSaver(true);
      }, screenSaverTimeout || IDDLE_TIME);
    }
  };

  // WATCHER: Observe iddle screen
  useEffect(() => {
    startTimer();

    window.addEventListener("click", () => {
      if (location.pathname !== "/" + ERoute.VIDEO) {
        const extractVideoExtention = window.location.href.split(".")[1];

        const isVideo = SUPPORTED_VIDEO_EXTENSION.includes(
          extractVideoExtention
        );

        if (!isVideo) {
          startTimer();
        }
      }
    });

    if (videoRef) {
      videoRef.addEventListener("playing", () => {
        clearTimeout(timeOutRef.current);
      });

      videoRef.addEventListener("pause", () => {
        startTimer();
      });
    }

    return () => {
      clearTimeout(timeOutRef.current);
    };
  }, [videoRef, location]);

  if (isShowScreenSaver) {
    return <ScreenSaverPage toggleScreeSaver={toggleScreeSaver} />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Grid
          container
          direction="column"
          justifyContent="start"
          height="100%"
          wrap="nowrap"
        >
          <Header />
          <Grid
            container
            item
            xs={12}
            direction="column"
            justifyContent="start"
            alignItems="center"
            height="100%"
            wrap="nowrap"
            padding={{
              xs: 2,
              lg: 4,
            }}
          >
            {renderOfflineAlert()}
            <Outlet />
          </Grid>
          <Disclaimer />
        </Grid>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
